import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheckCircle,
  faCircle,
  faClock,
  faLock,
  faLockOpen,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import CourseBreadcrumb from '@/components/videos/CourseBreadcrumb';
import VideoChatPanel from '@/components/videos/VideoChatPanel';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { VideoAttachments } from '@/components/videos/VideoAttachments';
import { APIRoutes } from '@/services/api.routes';
import { APIRequestError } from '@/services/api';
import { courseProgressService } from '@/services/courseProgress.service';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import FullscreenError from '@/components/utils/FullscreenError';
import type { Course, Module, Video, CourseID, ModuleID, VideoID } from '@/models';
import BunnyVideoPlayer from "@/components/videos/BunnyVideoPlayer";

const VideoDetailView: React.FC = () => {
  const { courseId, moduleId, videoId } = useParams<{
    courseId: string;
    moduleId: string;
    videoId: string;
  }>();

  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [video, setVideo] = useState<Video | null>(null);
  const [allVideos, setAllVideos] = useState<Video[]>([]);
  const [isWatched, setIsWatched] = useState(false);
  const [isVideoSticky, setIsVideoSticky] = useState(true);
  // 'locked' = Kurs nicht freigeschaltet (Backend: FEATURE_DISABLED), sonst technischer Fehler.
  // Ohne diesen State würde die Seite dauerhaft im "Lade Video..."-Zustand hängen.
  const [loadError, setLoadError] = useState<'locked' | 'failed' | null>(null);
  const { hasFeature } = useFeatureAccess();

  useEffect(() => {
    if (courseId && moduleId && videoId) {
      fetchVideoData();
    }
  }, [courseId, moduleId, videoId]);

  const fetchVideoData = async () => {
    if (!courseId || !moduleId || !videoId) return;

    setLoadError(null);

    try {
      const [courseResponse, moduleResponse, videoResponse, videosResponse] = await Promise.all([
        APIRoutes.getCourse(courseId as CourseID),
        APIRoutes.getModule(moduleId as ModuleID),
        APIRoutes.getVideo(videoId as VideoID),
        APIRoutes.listVideos(moduleId as ModuleID, { published: true })
      ]);

      setCourse(courseResponse);
      // Handle both wrapped and direct module responses
      setModule(moduleResponse);
      setVideo(videoResponse);
      setAllVideos(videosResponse.videos);
      
      // Check watched status asynchronously
      try {
        const watchedStatus = await courseProgressService.isWatched(courseId, moduleId, videoId);
        setIsWatched(watchedStatus);
      } catch (error) {
        console.error('Error checking video watch status:', error);
        // Fallback to sync method
        setIsWatched(courseProgressService.isWatchedSync(courseId, moduleId, videoId));
      }
    } catch (err) {
      console.error('Error loading video:', err);
      setCourse(null);
      setModule(null);
      setVideo(null);
      const isLocked = err instanceof APIRequestError && err.errorcode === 'FEATURE_DISABLED';
      setLoadError(isLocked ? 'locked' : 'failed');
    }
  };

  // Don't redirect immediately, wait for the data to load
  if (!courseId || !moduleId || !videoId) {
    return <Navigate to="/courses" replace />;
  }

  // Kurs nicht freigeschaltet -> auf die Kurs-gesperrt-Seite statt endlos "Lade Video..."
  if (loadError === 'locked') {
    return <Navigate to={`/feature-locked/course/${courseId}`} replace />;
  }

  if (loadError === 'failed') {
    return (
      <FullscreenError
        title="Video nicht verfügbar"
        message="Dieses Video konnte nicht geladen werden. Bitte versuche es erneut."
        onRetry={() => { void fetchVideoData(); }}
        showBack={false}
      />
    );
  }

  // If data is still loading, show loading state instead of redirecting
  if (!course || !module || !video) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Lade Video...</p>
        </div>
      </>
    );
  }

  const toggleWatched = async () => {
    try {
      if (isWatched) {
        await courseProgressService.markAsUnwatched(courseId, moduleId, videoId);
        // Reload the watched status from the server to ensure consistency
        const newWatchedStatus = await courseProgressService.isWatched(courseId, moduleId, videoId);
        setIsWatched(newWatchedStatus);
      } else {
        await courseProgressService.markAsWatched(courseId, moduleId, videoId);
        // Reload the watched status from the server to ensure consistency
        const newWatchedStatus = await courseProgressService.isWatched(courseId, moduleId, videoId);
        setIsWatched(newWatchedStatus);
      }
    } catch (error) {
      console.error('Error toggling watched status:', error);
    }
  };

  const findAdjacentVideos = () => {
    const currentIndex = allVideos.findIndex((v) => v.id === videoId);
    const prevVideo = currentIndex > 0 ? allVideos[currentIndex - 1] : null;
    const nextVideo = currentIndex < allVideos.length - 1 ? allVideos[currentIndex + 1] : null;
    return { prevVideo, nextVideo };
  };

  const { prevVideo, nextVideo } = findAdjacentVideos();

  // Check if chat panel should be shown
  const showChatPanel = hasFeature('videoChatbot') && video.providesKnowledge;

  const formatDuration = (seconds: number|null) => {
    if (!seconds) return 'Unbekannt';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressUpdate = async (progress: number) => {
    // When video is completed (progress >= 1.0), refresh the watched status
    if (progress >= 1.0 && !isWatched) {
      try {
        // Clear cache to ensure we get fresh data from server
        courseProgressService.clearCache();

        // Small delay to ensure server has processed the update
        await new Promise(resolve => setTimeout(resolve, 300));

        // Now fetch the updated watched status from server
        const watchedStatus = await courseProgressService.isWatched(courseId!, moduleId!, videoId!);
        setIsWatched(watchedStatus);
      } catch (error) {
        console.error('Error refreshing watched status:', error);
      }
    }
  };

  return (
    <>
      <div className="max-w-[1920px] mx-auto">
        <CourseBreadcrumb
          items={[
            { label: 'Kurse', href: '/courses' },
            { label: course.title, href: `/courses/${courseId}` },
            { label: module.title, href: `/courses/${courseId}/modules/${moduleId}` },
            { label: video.title }
          ]}
        />

        <div className={`grid grid-cols-1 gap-6 ${showChatPanel ? 'lg:grid-cols-[1fr_380px]' : ''}`}>
          <div className="space-y-6">
            <div className={isVideoSticky ? "sticky top-4" : ""}>
              <div className="relative">
                <button
                  onClick={() => setIsVideoSticky(!isVideoSticky)}
                  className="absolute top-2 right-2 z-10 bg-black/70 hover:bg-black/90 text-white px-3 py-2 rounded-lg transition-colors cursor-pointer flex items-center gap-2 text-sm"
                  title={isVideoSticky ? "Video scrollt mit" : "Video fixieren"}
                >
                  <FontAwesomeIcon icon={isVideoSticky ? faLock : faLockOpen} className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {isVideoSticky ? "Fixiert" : "Scrollt mit"}
                  </span>
                </button>
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  <BunnyVideoPlayer video={video} onProgressUpdate={handleProgressUpdate} />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {video.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} className="w-4 h-4" />
                      {formatDuration(video.duration)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={toggleWatched}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 cursor-pointer ${
                    isWatched
                      ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <FontAwesomeIcon icon={isWatched ? faCheckCircle : faCircle} className="w-4 h-4" />
                  {isWatched ? 'Als gesehen markiert' : 'Als gesehen markieren'}
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mb-6">
                <Link
                  to={`/courses/${courseId}/modules/${moduleId}`}
                  className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer"
                  title="Zurück zur Übersicht"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
                  <span className="hidden sm:inline">Zurück zur Übersicht</span>
                </Link>

                {prevVideo && (
                  <Link
                    to={`/courses/${courseId}/modules/${moduleId}/videos/${prevVideo.id}`}
                    className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer"
                    title="Vorheriges Video"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                    <span className="hidden sm:inline">Vorheriges Video</span>
                  </Link>
                )}

                {nextVideo && (
                  <Link
                    to={`/courses/${courseId}/modules/${moduleId}/videos/${nextVideo.id}`}
                    className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer"
                    title="Nächstes Video"
                  >
                    <span className="hidden sm:inline">Nächstes Video</span>
                    <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                  </Link>
                )}
              </div>

              <MarkdownRenderer>
                {video.description}
              </MarkdownRenderer>

              <VideoAttachments videoId={videoId as VideoID} />

            </div>
          </div>

          {showChatPanel && (
            <div className="lg:sticky lg:top-4 h-[calc(100vh-12rem)] max-h-[700px] flex flex-col">
              <VideoChatPanel
                videoId={videoId}
                videoTitle={video.title}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoDetailView;
